import React from "react";
import { Box, Button, Dialog, DialogContent, useMediaQuery } from "@mui/material";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt, StatusBadgeColorConfig } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import { OfferStatusCode } from "../models/models";
import { createOfferStatusLabelMap } from "../utils/offerStatusMaps";
import { statusBadgeColors } from "./OfferCard";
import RedesignStatusBadge from "../../../shared/components/redesign/RedesignStatusBadge";
import dayjs from "dayjs";

interface DuplicateOfferDialogProps {
  open: boolean;
  onClose: () => void;
  existingOffer: {
    id: string;
    statusCode: number;
    laborCost: number;
    totalEstimatedCost: number;
    createdAt: string;
    submittedAt?: string;
    serviceApproach?: string;
  };
  onReplaceOffer: () => void;
  onViewExistingOffer: () => void;
}

const DuplicateOfferDialog = ({
  open,
  onClose,
  existingOffer,
  onReplaceOffer,
  onViewExistingOffer,
}: DuplicateOfferDialogProps) => {
  const { translate } = useTranslation(["serviceOffers", "common"]);
  const isPhone = useMediaQuery("(max-width:600px)");

  useRedesignSetup();

  const statusLabelMap = createOfferStatusLabelMap(translate);
  const colorConfig: StatusBadgeColorConfig =
    statusBadgeColors[existingOffer.statusCode] ||
    statusBadgeColors[OfferStatusCode.draft];
  const statusLabel =
    statusLabelMap[existingOffer.statusCode as OfferStatusCode] ||
    translate("duplicateOffer.draftLabel");

  const formatEur = (val: number) =>
    new Intl.NumberFormat("en-EU", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(val);

  const dateLabel = existingOffer.submittedAt
    ? dayjs(existingOffer.submittedAt).format("MMM DD, YYYY")
    : existingOffer.createdAt
    ? dayjs(existingOffer.createdAt).format("MMM DD, YYYY")
    : "";

  const dateKey = existingOffer.submittedAt
    ? translate("duplicateOffer.submitted")
    : translate("duplicateOffer.created");

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: dt.radius,
          overflow: "hidden",
          animation: "slideUp 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          "@keyframes slideUp": {
            "0%": { opacity: 0, transform: "translateY(20px)" },
            "100%": { opacity: 1, transform: "translateY(0)" },
          },
        },
      }}
    >
      {/* Blue gradient accent bar */}
      <Box
        sx={{
          height: "5px",
          background:
            "linear-gradient(90deg, #3B82F6 0%, #2563EB 50%, #1D4ED8 100%)",
        }}
      />

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          px: isPhone ? 3 : 4,
          py: 4,
          gap: 2,
        }}
      >
        {/* Icon in blue circle with pulse */}
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "rgba(59, 130, 246, 0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "iconPulse 2s ease-in-out infinite",
            "@keyframes iconPulse": {
              "0%, 100%": { transform: "scale(1)" },
              "50%": { transform: "scale(1.06)" },
            },
          }}
        >
          <FileCopyOutlinedIcon sx={{ fontSize: 32, color: "#2563EB" }} />
        </Box>

        {/* Title */}
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "20px",
            fontWeight: 700,
            color: dt.textPrimary,
            letterSpacing: "-0.01em",
          }}
        >
          {translate("duplicateOffer.title")}
        </Box>

        {/* Body */}
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "14px",
            fontWeight: 400,
            color: dt.textSecondary,
            lineHeight: 1.6,
            maxWidth: "420px",
          }}
        >
          {translate("duplicateOffer.body")}
        </Box>

        {/* Existing offer summary card */}
        <Box
          sx={{
            width: "100%",
            borderLeft: "4px solid #3B82F6",
            borderRadius: "8px",
            border: `1px solid ${dt.cardBorder}`,
            borderLeftWidth: "4px",
            borderLeftStyle: "solid",
            borderLeftColor: "#3B82F6",
            backgroundColor: "#FAFBFC",
            padding: "16px",
            mt: 1,
          }}
        >
          {/* Card header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                color: dt.textMuted,
              }}
            >
              {translate("duplicateOffer.currentOffer")}
            </Box>
            <RedesignStatusBadge label={statusLabel} colorConfig={colorConfig} />
          </Box>

          {/* Card details */}
          <Box
            sx={{
              display: "flex",
              flexDirection: isPhone ? "column" : "row",
              gap: isPhone ? 1 : 3,
              alignItems: isPhone ? "flex-start" : "center",
            }}
          >
            {/* Price */}
            <Box>
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "11px",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  color: dt.textMuted,
                  mb: "2px",
                }}
              >
                {translate("duplicateOffer.price")}
              </Box>
              <Box
                sx={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#1E293B",
                }}
              >
                {formatEur(existingOffer.totalEstimatedCost)}
              </Box>
            </Box>

            {/* Date */}
            <Box>
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "11px",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  color: dt.textMuted,
                  mb: "2px",
                }}
              >
                {dateKey}
              </Box>
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#1E293B",
                }}
              >
                {dateLabel}
              </Box>
            </Box>

            {/* Service Approach (truncated) */}
            {existingOffer.serviceApproach && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "11px",
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: dt.textMuted,
                    mb: "2px",
                  }}
                >
                  {translate("serviceApproach")}
                </Box>
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "13px",
                    fontWeight: 400,
                    color: "#475569",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    textAlign: "left",
                  }}
                >
                  {existingOffer.serviceApproach}
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Info hint box */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            background: "#EFF6FF",
            borderLeft: "3px solid #3B82F6",
            borderRadius: "6px",
            padding: "12px 14px",
            width: "100%",
            mt: 0.5,
          }}
        >
          <InfoOutlinedIcon
            sx={{
              fontSize: 18,
              color: "#2563EB",
              mt: "1px",
              flexShrink: 0,
            }}
          />
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              fontWeight: 400,
              color: "#1E40AF",
              lineHeight: 1.5,
              textAlign: "left",
            }}
          >
            {translate("duplicateOffer.hint")}
          </Box>
        </Box>

        {/* Actions */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isPhone ? "column-reverse" : "row",
            gap: 1.5,
            width: "100%",
            mt: 1,
            justifyContent: "center",
          }}
        >
          {/* Cancel */}
          <Button
            variant="text"
            onClick={onClose}
            sx={{
              fontFamily: dt.font,
              fontWeight: 600,
              fontSize: "14px",
              textTransform: "none",
              borderRadius: "8px",
              px: 2.5,
              py: 1,
              color: dt.textSecondary,
              ...(isPhone && { width: "100%" }),
              "&:hover": {
                backgroundColor: "rgba(107, 114, 128, 0.04)",
              },
            }}
          >
            {translate("common:buttons.cancel")}
          </Button>

          {/* View Existing Offer */}
          <Button
            variant="outlined"
            onClick={onViewExistingOffer}
            sx={{
              fontFamily: dt.font,
              fontWeight: 600,
              fontSize: "14px",
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1,
              color: "#2563EB",
              borderColor: "#93C5FD",
              ...(isPhone && { width: "100%" }),
              "&:hover": {
                borderColor: "#2563EB",
                backgroundColor: "rgba(59, 130, 246, 0.04)",
              },
            }}
          >
            {translate("duplicateOffer.viewExisting")}
          </Button>

          {/* Replace with New Offer */}
          <Button
            variant="contained"
            onClick={onReplaceOffer}
            sx={{
              fontFamily: dt.font,
              fontWeight: 600,
              fontSize: "14px",
              textTransform: "none",
              borderRadius: "24px",
              px: 3,
              py: 1,
              background:
                "linear-gradient(135deg, #3B82F6, #2563EB)",
              boxShadow: "none",
              ...(isPhone && { width: "100%" }),
              "&:hover": {
                background:
                  "linear-gradient(135deg, #2563EB, #1D4ED8)",
                boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)",
              },
            }}
          >
            {translate("duplicateOffer.replaceOffer")}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateOfferDialog;
